// Visual Query Builder — BOUNDED round-trip parser (SQL → model).
//
// This is the deliberately-restricted inverse of qbsql.buildSQL. It recognises
// ONLY the shape the builder itself emits — a single SELECT over aliased tables
// with simple joins, comparison filters, aggregates, GROUP/ORDER BY, DISTINCT,
// LIMIT — and REFUSES everything else (subqueries, UNION, CTEs, HAVING, window
// functions, unknown expressions, …) with a human reason. Refusing is the whole
// point: we never half-parse a query into a model that would silently change it.
// So `parseSelect` is safe to point at any SQL — worst case it declines.
//
// parseSelect(sql) → { ok:true, model } | { ok:false, reason }
// where model matches qbsql.js: { tables, joins, columns, filters, distinct, limit }.

import { AGGREGATES, OPERATORS, renderValue, valueText } from './qbsql.js';

const AGG_NAMES = new Set(AGGREGATES.filter((a) => a.v && a.v !== 'COUNT_DISTINCT').map((a) => a.v));
const OP_SET = new Set(OPERATORS.map((o) => o.v));

// ── tokenizer ────────────────────────────────────────────────────────────────
// MySQL string escapes (the char after a backslash). `\%` / `\_` keep their
// backslash — they're LIKE-pattern escapes, not string ones.
const ESCAPES = { 0: '\0', b: '\b', n: '\n', r: '\r', t: '\t', Z: '\x1a', '%': '\\%', _: '\\_' };
const unescape = (ch) => ESCAPES[ch] ?? ch;

function tokenize(sql) {
  const toks = [];
  let i = 0;
  const n = sql.length;
  let bad = null;
  while (i < n) {
    const c = sql[i];
    if (/\s/.test(c)) { i++; continue; }
    if (c === '-' && sql[i + 1] === '-') { while (i < n && sql[i] !== '\n') i++; continue; }
    if (c === '#') { while (i < n && sql[i] !== '\n') i++; continue; }
    if (c === '/' && sql[i + 1] === '*') { i += 2; while (i < n && !(sql[i] === '*' && sql[i + 1] === '/')) i++; i += 2; continue; }
    if (c === '`') {
      let j = i + 1, v = '';
      while (j < n) { if (sql[j] === '`') { if (sql[j + 1] === '`') { v += '`'; j += 2; continue; } break; } v += sql[j]; j++; }
      toks.push({ t: 'id', v }); i = j + 1; continue;
    }
    if (c === "'" || c === '"') {
      const q = c; let j = i + 1, v = '';
      while (j < n) { if (sql[j] === '\\') { v += unescape(sql[j + 1] ?? ''); j += 2; continue; } if (sql[j] === q) { if (sql[j + 1] === q) { v += q; j += 2; continue; } break; } v += sql[j]; j++; }
      toks.push({ t: 'str', v }); i = j + 1; continue;
    }
    if (/[0-9]/.test(c) || (c === '.' && /[0-9]/.test(sql[i + 1] || ''))) {
      let j = i; while (j < n && /[0-9.]/.test(sql[j])) j++;
      // allow a leading sign only when it's clearly a number (handled by parser context); keep digits here
      toks.push({ t: 'num', v: sql.slice(i, j) }); i = j; continue;
    }
    if (/[A-Za-z_$@]/.test(c)) {
      let j = i; while (j < n && /[A-Za-z0-9_$@]/.test(sql[j])) j++;
      toks.push({ t: 'word', v: sql.slice(i, j) }); i = j; continue;
    }
    const two = sql.slice(i, i + 2);
    if (two === '<=' || two === '>=' || two === '<>') { toks.push({ t: 'op', v: two }); i += 2; continue; }
    if (two === '!=') { toks.push({ t: 'op', v: '<>' }); i += 2; continue; }
    if (c === '=' || c === '<' || c === '>') { toks.push({ t: 'op', v: c }); i++; continue; }
    if (c === '-' || c === '+') { toks.push({ t: 'sign', v: c }); i++; continue; }
    if ('(),.*;'.includes(c)) { toks.push({ t: 'punct', v: c }); i++; continue; }
    bad = c; break;
  }
  return { toks, bad };
}

// ── parser ───────────────────────────────────────────────────────────────────
const decline = (reason) => ({ ok: false, reason });

export function parseSelect(sql) {
  if (!sql || !sql.trim()) return decline('Empty query.');
  const { toks, bad } = tokenize(sql);
  if (bad) return decline(`Unsupported character “${bad}”.`);
  // strip a single trailing ';'
  while (toks.length && toks[toks.length - 1].t === 'punct' && toks[toks.length - 1].v === ';') toks.pop();
  if (toks.some((t) => t.t === 'punct' && t.v === ';')) return decline('Only a single statement can be edited visually.');
  if (!toks.length) return decline('Empty query.');

  let p = 0;
  const peek = () => toks[p];
  const next = () => toks[p++];
  const isWord = (w) => peek() && peek().t === 'word' && peek().v.toUpperCase() === w;
  const eatWord = (w) => { if (isWord(w)) { p++; return true; } return false; };
  const atClauseEnd = () => {
    const t = peek();
    if (!t) return true;
    if (t.t === 'word') { const u = t.v.toUpperCase(); return ['FROM', 'WHERE', 'GROUP', 'ORDER', 'LIMIT', 'HAVING', 'UNION'].includes(u); }
    return false;
  };

  // unsupported leading forms
  if (isWord('WITH')) return decline('Common table expressions (WITH) can’t be edited visually.');
  if (!eatWord('SELECT')) return decline('Only SELECT queries can be edited visually.');

  const model = { tables: [], joins: [], columns: [], filters: { op: 'AND', items: [] }, distinct: false, limit: null };
  if (eatWord('DISTINCT')) model.distinct = true;
  if (isWord('DISTINCTROW')) return decline('DISTINCTROW isn’t supported.');

  // ── SELECT list ─────────────────────────────────────────────────────────────
  // Parsed into raw items first; tables aren't known until FROM, so we resolve
  // qualifiers afterwards.
  const rawCols = [];
  let starOnly = false;
  for (;;) {
    if (peek() && peek().t === 'punct' && peek().v === '*') { next(); starOnly = true; }
    else {
      const item = parseSelectItem();
      if (item.err) return decline(item.err);
      rawCols.push(item);
    }
    if (peek() && peek().t === 'punct' && peek().v === ',') { next(); continue; }
    break;
  }
  if (!isWord('FROM')) return decline('A FROM clause is required.');
  next();

  // ── FROM + JOINs ────────────────────────────────────────────────────────────
  const aliasToTable = new Map(); // alias(lower) -> table name
  const addTable = (name, alias) => {
    const a = alias || name;
    if (model.tables.some((t) => t.alias.toLowerCase() === a.toLowerCase())) return decline(`Duplicate alias “${a}”.`);
    model.tables.push({ name, alias: a });
    aliasToTable.set(a.toLowerCase(), name);
    return null;
  };
  const root = parseTableRef();
  if (root.err) return decline(root.err);
  { const e = addTable(root.table, root.alias); if (e) return e; }

  for (;;) {
    let type = 'INNER';
    if (eatWord('INNER')) { type = 'INNER'; if (!eatWord('JOIN')) return decline('Expected JOIN.'); }
    else if (eatWord('LEFT')) { type = 'LEFT'; eatWord('OUTER'); if (!eatWord('JOIN')) return decline('Expected JOIN.'); }
    else if (eatWord('RIGHT')) { type = 'RIGHT'; eatWord('OUTER'); if (!eatWord('JOIN')) return decline('Expected JOIN.'); }
    else if (isWord('JOIN')) { next(); type = 'INNER'; }
    else if (isWord('CROSS') || isWord('NATURAL') || isWord('STRAIGHT_JOIN')) return decline('Only INNER/LEFT/RIGHT joins can be edited visually.');
    else break;

    const tr = parseTableRef();
    if (tr.err) return decline(tr.err);
    { const e = addTable(tr.table, tr.alias); if (e) return e; }
    if (!eatWord('ON')) return decline('Each join needs an ON condition.');
    // ON: one or more `a.c = b.c` ANDed together (only equality, only column=column)
    for (;;) {
      const l = parseColRef(); if (l.err) return decline(l.err);
      const op = peek();
      if (!op || op.t !== 'op' || op.v !== '=') return decline('Join conditions must be column = column.');
      next();
      const r = parseColRef(); if (r.err) return decline(r.err);
      model.joins.push({ left: { table: resolveTable(l), col: l.col }, right: { table: resolveTable(r), col: r.col }, type });
      if (eatWord('AND')) continue;
      break;
    }
  }

  // resolve a colref's table via alias map (or sole table); records errors
  let resolveErr = null;
  function resolveTable(ref) {
    if (ref.qualifier) {
      const t = aliasToTable.get(ref.qualifier.toLowerCase());
      if (!t) { resolveErr = `Unknown table/alias “${ref.qualifier}”.`; return ref.qualifier; }
      return t;
    }
    if (model.tables.length === 1) return model.tables[0].name;
    resolveErr = `Column “${ref.col}” must be qualified (it’s ambiguous across joined tables).`;
    return '';
  }

  // ── finalise SELECT columns (now that tables/aliases are known) ──────────────
  if (!starOnly) {
    for (const it of rawCols) {
      const table = resolveTable(it.ref);
      if (resolveErr) return decline(resolveErr);
      model.columns.push({ table, column: it.ref.col, alias: it.alias || '', agg: it.agg || '', show: true, sort: '', group: false });
    }
  }

  // ── WHERE ───────────────────────────────────────────────────────────────────
  if (eatWord('WHERE')) {
    const g = parseConditionGroup();
    if (g.err) return decline(g.err);
    model.filters = g.group;
    if (resolveErr) return decline(resolveErr);
  }

  // ── GROUP BY ────────────────────────────────────────────────────────────────
  if (isWord('GROUP')) {
    next(); if (!eatWord('BY')) return decline('Expected GROUP BY.');
    for (;;) {
      const r = parseColRef(); if (r.err) return decline(r.err);
      const table = resolveTable(r); if (resolveErr) return decline(resolveErr);
      const col = model.columns.find((c) => c.table === table && c.column === r.col && !c.agg);
      if (col) col.group = true;
      else model.columns.push({ table, column: r.col, alias: '', agg: '', show: false, sort: '', group: true });
      if (eatWord(',') || (peek() && peek().t === 'punct' && peek().v === ',' && next())) continue;
      break;
    }
  }

  // ── ORDER BY ────────────────────────────────────────────────────────────────
  if (isWord('ORDER')) {
    next(); if (!eatWord('BY')) return decline('Expected ORDER BY.');
    for (;;) {
      const item = parseSelectItem(); // ORDER BY expr can be col or AGG(col)
      if (item.err) return decline(item.err);
      let dir = 'ASC';
      if (eatWord('DESC')) dir = 'DESC'; else eatWord('ASC');
      const table = resolveTable(item.ref); if (resolveErr) return decline(resolveErr);
      // attach sort to an existing matching column, else add a hidden one
      let col = model.columns.find((c) => c.table === table && c.column === item.ref.col && (c.agg || '') === (item.agg || ''));
      if (!col) { col = { table, column: item.ref.col, alias: '', agg: item.agg || '', show: false, sort: '', group: false }; model.columns.push(col); }
      col.sort = dir;
      if (peek() && peek().t === 'punct' && peek().v === ',') { next(); continue; }
      break;
    }
  }

  // ── LIMIT ───────────────────────────────────────────────────────────────────
  if (eatWord('LIMIT')) {
    const t = peek();
    if (!t || t.t !== 'num' || t.v.includes('.')) return decline('LIMIT must be a whole number.');
    next();
    model.limit = parseInt(t.v, 10);
    // reject `LIMIT a, b` (offset form) — the builder doesn't model offset
    if (peek() && peek().t === 'punct' && peek().v === ',') return decline('LIMIT with an offset isn’t supported.');
  }

  if (eatWord('HAVING') || isWord('HAVING')) return decline('HAVING isn’t supported by the visual builder yet.');
  if (isWord('UNION')) return decline('UNION queries can’t be edited visually.');
  if (p < toks.length) return decline('Unsupported SQL near the end of the query.');
  if (model.tables.length === 0) return decline('No table found.');

  return { ok: true, model };

  // ── sub-parsers (closures over toks/p) ──────────────────────────────────────
  // a select item: [AGG ( [DISTINCT] colref )] | colref, optional [AS] alias
  function parseSelectItem() {
    let agg = '';
    const w = peek();
    if (w && w.t === 'word') {
      const up = w.v.toUpperCase();
      const after = toks[p + 1];
      if ((AGG_NAMES.has(up) || up === 'COUNT') && after && after.t === 'punct' && after.v === '(') {
        next(); next(); // name (
        if (up === 'COUNT' && eatWord('DISTINCT')) agg = 'COUNT_DISTINCT';
        else agg = up;
        // COUNT(*) special case
        if (up === 'COUNT' && agg === 'COUNT' && peek() && peek().t === 'punct' && peek().v === '*') {
          next();
          if (!(peek() && peek().t === 'punct' && peek().v === ')')) return { err: 'Unsupported expression in the column list.' };
          next();
          const al = parseAlias();
          return { ref: { qualifier: null, col: '*' }, agg: 'COUNT', alias: al, countStar: true };
        }
        const ref = parseColRef();
        if (ref.err) return { err: ref.err };
        if (!(peek() && peek().t === 'punct' && peek().v === ')')) return { err: 'Only a single column is allowed inside an aggregate.' };
        next();
        const al = parseAlias();
        return { ref, agg, alias: al };
      }
    }
    const ref = parseColRef();
    if (ref.err) return { err: ref.err };
    const al = parseAlias();
    return { ref, agg: '', alias: al };
  }

  // optional [AS] <identifier> alias following a select/order item
  function parseAlias() {
    if (eatWord('AS')) { const t = next(); return t && (t.t === 'id' || t.t === 'word') ? t.v : ''; }
    const t = peek();
    if (t && (t.t === 'id') ) { next(); return t.v; }   // backticked alias
    if (t && t.t === 'word' && !atClauseEnd() && !isReservedFollow(t.v)) { next(); return t.v; }
    return '';
  }
  function isReservedFollow(w) {
    return ['ASC', 'DESC', 'AND', 'OR', 'ON', 'AS', 'INNER', 'LEFT', 'RIGHT', 'JOIN', 'CROSS', 'NATURAL', 'WHERE', 'GROUP', 'ORDER', 'LIMIT', 'HAVING', 'UNION'].includes(w.toUpperCase());
  }

  // a column reference: [qualifier .] col   (identifiers or backticked)
  function parseColRef() {
    const a = peek();
    if (!a || (a.t !== 'word' && a.t !== 'id')) return { err: 'Expected a column.' };
    if (a.t === 'word' && isReservedFollow(a.v)) return { err: 'Expected a column.' };
    next();
    if (peek() && peek().t === 'punct' && peek().v === '.') {
      next();
      const b = peek();
      if (!b || (b.t !== 'word' && b.t !== 'id')) {
        // qualifier.* → unsupported in our model
        return { err: 'Unsupported column expression.' };
      }
      next();
      return { qualifier: a.v, col: b.v };
    }
    return { qualifier: null, col: a.v };
  }

  // a table reference: table [ [AS] alias ]   (db.table not modelled → decline)
  function parseTableRef() {
    const a = peek();
    if (a && a.t === 'punct' && a.v === '(') return { err: 'Derived tables / subqueries can’t be edited visually.' };
    if (!a || (a.t !== 'word' && a.t !== 'id')) return { err: 'Expected a table name.' };
    next();
    if (peek() && peek().t === 'punct' && peek().v === '.') return { err: 'Database-qualified tables can’t be edited visually yet.' };
    let alias = null;
    if (eatWord('AS')) { const t = next(); if (!t || (t.t !== 'word' && t.t !== 'id')) return { err: 'Expected an alias.' }; alias = t.v; }
    else { const t = peek(); if (t && (t.t === 'id' || (t.t === 'word' && !isReservedFollow(t.v)))) { next(); alias = t.v; } }
    return { table: a.v, alias };
  }

  // a condition group: cond [ (AND|OR) cond ]*  with parenthesised sub-groups.
  // Mixed AND/OR at the same level is declined (the builder models one op/group).
  function parseConditionGroup() {
    const items = [];
    let groupOp = null;
    for (;;) {
      let item;
      if (peek() && peek().t === 'punct' && peek().v === '(') {
        next();
        const sub = parseConditionGroup();
        if (sub.err) return sub;
        if (!(peek() && peek().t === 'punct' && peek().v === ')')) return { err: 'Unbalanced parentheses in WHERE.' };
        next();
        item = sub.group;
      } else {
        const c = parseCondition();
        if (c.err) return c;
        item = c.cond;
      }
      items.push(item);
      const t = peek();
      if (t && t.t === 'word' && (t.v.toUpperCase() === 'AND' || t.v.toUpperCase() === 'OR')) {
        const op = t.v.toUpperCase();
        if (groupOp && groupOp !== op) return { err: 'Mixed AND/OR at the same level — wrap one side in parentheses.' };
        groupOp = op; next(); continue;
      }
      break;
    }
    return { group: { op: groupOp || 'AND', items } };
  }

  // a single condition: colref OP value | colref IS [NOT] NULL | colref [NOT] IN (..) | colref BETWEEN a AND b
  function parseCondition() {
    const ref = parseColRef();
    if (ref.err) return ref;
    const table = resolveTable(ref);
    const base = { table, column: ref.col };
    const t = peek();
    if (!t) return { err: 'Incomplete condition.' };
    // IS [NOT] NULL
    if (t.t === 'word' && t.v.toUpperCase() === 'IS') {
      next();
      const notNull = eatWord('NOT');
      if (!eatWord('NULL')) return { err: 'Expected NULL after IS.' };
      return { cond: { ...base, op: notNull ? 'IS NOT NULL' : 'IS NULL', value: '', value2: '' } };
    }
    // [NOT] IN ( ... )
    if (t.t === 'word' && (t.v.toUpperCase() === 'IN' || t.v.toUpperCase() === 'NOT')) {
      let op = 'IN';
      if (t.v.toUpperCase() === 'NOT') { next(); if (!eatWord('IN')) return { err: 'Expected IN after NOT.' }; op = 'NOT IN'; }
      else next();
      if (!(peek() && peek().t === 'punct' && peek().v === '(')) return { err: 'Expected ( after IN.' };
      next();
      const vals = [];
      for (;;) {
        const v = parseScalar(true); if (v.err) return v;
        vals.push(v.text);
        if (peek() && peek().t === 'punct' && peek().v === ',') { next(); continue; }
        break;
      }
      if (!(peek() && peek().t === 'punct' && peek().v === ')')) return { err: 'Expected ) to close IN list.' };
      next();
      return { cond: { ...base, op, value: vals.join(', '), value2: '' } };
    }
    // [NOT] LIKE
    if (t.t === 'word' && (t.v.toUpperCase() === 'LIKE')) {
      next(); const v = parseScalar(); if (v.err) return v;
      return { cond: { ...base, op: 'LIKE', value: v.text, value2: '' } };
    }
    // BETWEEN a AND b
    if (t.t === 'word' && t.v.toUpperCase() === 'BETWEEN') {
      next(); const a = parseScalar(); if (a.err) return a;
      if (!eatWord('AND')) return { err: 'Expected AND in BETWEEN.' };
      const b = parseScalar(); if (b.err) return b;
      return { cond: { ...base, op: 'BETWEEN', value: a.text, value2: b.text } };
    }
    // comparison operator
    if (t.t === 'op' && OP_SET.has(t.v)) {
      next(); const v = parseScalar(); if (v.err) return v;
      return { cond: { ...base, op: t.v, value: v.text, value2: '' } };
    }
    return { err: 'Unsupported condition.' };
  }

  // a scalar literal in a condition value: number (opt sign), string, NULL/TRUE/
  // FALSE, or @var. Column-on-the-right (a.x = b.y in WHERE) isn't modelled →
  // decline so we never silently drop it. The returned text is value-box text
  // that qbsql.renderValue turns back into the same literal: a string keeps its
  // quotes when it would otherwise read as a number/keyword ('007', 'NULL').
  function parseScalar(inList = false) {
    let t = peek();
    let sign = '';
    if (t && t.t === 'sign') {
      sign = t.v === '-' ? '-' : ''; next(); t = peek();
      if (!t || t.t !== 'num') return { err: 'Expected a number after the sign.' };
    }
    if (!t) return { err: 'Expected a value.' };
    if (t.t === 'num') {
      next();
      if (renderValue(sign + t.v) !== sign + t.v) return { err: `Unsupported number “${t.v}”.` };
      return { text: sign + t.v };
    }
    if (t.t === 'str') { next(); return { text: valueText(t.v, inList) }; }
    if (t.t === 'word') {
      const up = t.v.toUpperCase();
      if (up === 'NULL' || up === 'TRUE' || up === 'FALSE') { next(); return { text: up }; }
      if (t.v.startsWith('@')) { next(); return { text: t.v }; }
      // a bare word that's a column reference on the RHS → not modelled
      return { err: 'Only literal values are supported on the right of a filter.' };
    }
    return { err: 'Expected a value.' };
  }
}
